import React, { memo, useCallback, useState } from 'react';
import type { CollectionUUID } from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import { ENTITY_LIMIT_TYPE } from '@jira/polaris__domain-collection/src/entityLimitType.constants.tsx';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import { PermissionsContainer } from '@jira/polaris__component-permissions-store/src/PermissionsContainer.tsx';
import { getCollectionContainerScope } from '@jira/polaris__component-permissions-store/src/getProjectContainerScope.tsx';
import { EntityLimitsContainer } from '@jira/polaris__component-entity-limits-store/src/EntityLimitsContainer.tsx';
import { useEntityLimits } from '@jira/polaris__component-entity-limits-store/src/useEntityLimitsByType.tsx';
import { NavigationContainer } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { graphql, useFragment } from 'react-relay';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { ActionButtonCreateView } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/ui/action-button-create-view/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JpdNavViewsContainer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/controllers/index.tsx';
import type { NavigationCollection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/types.tsx';
import { getViewUrl } from './common/utils/router.tsx';
import { useNavigationViewRemote } from './controllers/remotes/navigation/index.tsx';
import { useRoadmapsAnalyticsL2 } from './common/controllers/analytics/use-roadmaps-analytics-l2/index.tsx';
import { useRoadmapsAnalyticsL3 } from './common/controllers/analytics/use-roadmaps-analytics-l3/index.tsx';
import { useRoadmapsAnalyticsL4 } from './common/controllers/analytics/use-roadmaps-analytics-l4/index.tsx';
import { useIsReadOnly } from './common/utils/use-is-read-only.tsx';
import { PERMISSIONS_CACHE_TTL } from './common/constants.tsx';
import type { ActionButtonsRoadmap_collection$key } from './__generated__/ActionButtonsRoadmap_collection.graphql';
import { RoadmapMenuItems } from './RoadmapMenuItems.tsx';

export type ActionButtonsRoadmapProps = {
	collectionUUID: CollectionUUID;
	isCollectionExpanded: boolean;
	isCollectionSelected: boolean;
	navigationCollection: NavigationCollection;
	onViewCreated: (view: NavigationView) => void;
	collectionQueryRefFragment?: ActionButtonsRoadmap_collection$key;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};
export const ActionButtonsRoadmap: React.MemoExoticComponent<
	({
		collectionUUID,
		isCollectionExpanded,
		isCollectionSelected,
		navigationCollection,
		onViewCreated,
		collectionQueryRefFragment,
		currentItems,
		currentOrder,
		item,
	}: ActionButtonsRoadmapProps) => React.JSX.Element
> = memo(
	({
		collectionUUID,
		isCollectionExpanded,
		isCollectionSelected,
		navigationCollection,
		onViewCreated,
		collectionQueryRefFragment,
		currentItems,
		currentOrder,
		item,
	}: ActionButtonsRoadmapProps): React.JSX.Element => {
		const actionButtonsCollectionData = useFragment<ActionButtonsRoadmap_collection$key>(
			graphql`
				fragment ActionButtonsRoadmap_collection on JiraProductDiscoveryCollection {
					...RoadmapMenuItems_collection
				}
			`,
			collectionQueryRefFragment,
		);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const navigationViewRemote = useNavigationViewRemote();
		const cloudId = useCloudId();
		const [entityLimits] = useEntityLimits();

		const [preloadPermissions, setPreloadPermissions] = useState(false);
		const { sendMeatBallDropDownOnProjectsAnalyticsEvent } = useRoadmapsAnalyticsL2();

		const handlePermissionsPreloading = useCallback(() => {
			setPreloadPermissions(true);
		}, [setPreloadPermissions]);

		const menuItems = useCallback(
			({ onClose }: OnCloseProps) => (
				<RoadmapMenuItems
					collectionUUID={collectionUUID}
					preloadPermissions={preloadPermissions}
					onClose={onClose}
					collectionQueryRefFragment={actionButtonsCollectionData ?? undefined}
					currentItems={currentItems}
					currentOrder={currentOrder}
					item={item}
				/>
			),
			[
				actionButtonsCollectionData,
				collectionUUID,
				currentItems,
				currentOrder,
				item,
				preloadPermissions,
			],
		);

		const handleNavMenuOpen = useCallback(() => {
			sendMeatBallDropDownOnProjectsAnalyticsEvent(navigationCollection);
		}, [navigationCollection, sendMeatBallDropDownOnProjectsAnalyticsEvent]);

		return (
			<PermissionsContainer
				scope={getCollectionContainerScope(collectionUUID)}
				cacheTTL={PERMISSIONS_CACHE_TTL}
				isCollectionView
				isEmbeddedView={false}
				isSharedView={false}
			>
				{isCollectionExpanded && (
					<JpdNavViewsContainer<NavigationCollection>
						name="jpd-roadmaps"
						container={navigationCollection}
						viewsEntityLimitType={ENTITY_LIMIT_TYPE.VIEWS_PER_COLLECTION}
						viewsetsEntityLimitType={ENTITY_LIMIT_TYPE.VIEWSETS_PER_COLLECTION}
						sectionsEnabled
						containerTypePermissions="project"
						getViewUrl={getViewUrl}
						useIsReadOnly={useIsReadOnly}
						useAnalyticsL2={useRoadmapsAnalyticsL2}
						useAnalyticsL3={useRoadmapsAnalyticsL3}
						useAnalyticsL4={useRoadmapsAnalyticsL4}
					>
						<EntityLimitsContainer isGlobal createAnalyticsEvent={createAnalyticsEvent}>
							<NavigationContainer
								isGlobal
								containerType="COLLECTION"
								isActiveContainer={isCollectionSelected}
								navigationRemote={navigationViewRemote}
								cloudId={cloudId}
								containerId={collectionUUID}
								entityLimits={entityLimits}
								onViewCreated={onViewCreated}
								createAnalyticsEvent={createAnalyticsEvent}
							>
								<ActionButtonCreateView navigationContainer={navigationCollection} />
							</NavigationContainer>
						</EntityLimitsContainer>
					</JpdNavViewsContainer>
				)}
				{/* onFocus is used for keyboard navigation, onMouseEnter - for mouse to preload permissions for a collection */}
				<div onMouseEnter={handlePermissionsPreloading} onFocus={handlePermissionsPreloading}>
					<MoreNavMenuButton onOpen={handleNavMenuOpen} MenuItems={menuItems} />
				</div>
			</PermissionsContainer>
		);
	},
);
